/**
 * Record Display Name Utility
 *
 * Determines the display name for a record across different object types.
 * Checks for common name fields and falls back to other fields if needed.
 */

/**
 * Get the display name for a record
 * 
 * Priority order:
 * 1. Common name fields: name, title, subject, label
 * 2. First text field found
 * 3. Record ID as fallback
 */
export function getRecordDisplayName(objectApiName: string, record: Record<string, any>): string {
  if (!record) {
    return 'Unknown Record';
  }

  // Priority 1: Check common name fields
  const nameFields = ['name', 'title', 'subject', 'label', 'displayName'];
  for (const field of nameFields) {
    if (record[field] && typeof record[field] === 'string' && record[field].trim()) {
      return record[field].trim();
    }
  }

  // Priority 2: Find first text field (non-empty string)
  for (const [key, value] of Object.entries(record)) {
    // Skip metadata fields
    if (['id', 'createdDate', 'lastModified', 'createdBy', 'lastModifiedBy'].includes(key)) {
      continue;
    }
    
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  // Priority 3: Fall back to record ID
  return record.id || 'Unnamed Record';
}

/**
 * Get the primary field name for an object type (for display purposes)
 */
export function getPrimaryFieldName(objectApiName: string): string {
  // Common primary fields by object type
  const primaryFields: Record<string, string> = {
    'Client': 'name',
    'Contact': 'name',
    'Lead': 'name',
    'Opportunity': 'name',
    'Case': 'subject',
    'Task': 'subject',
    'Event': 'subject',
    'Report': 'name'
  };

  return primaryFields[objectApiName] || 'name';
}

